import {localeCodeToUrl, type LocaleCode} from '@commercial-website/data-layer/locale'

import type {Document} from '../types/document'
import type {LinkResolver} from '../types/link-resolver'
import {removeTrailingSlash} from './remove-trailing-slash'

export enum DocTypesEnum {
    ABOUT_US_PAGE = 'about_us_page',
    APPLE_PAY_PAGE = 'apple_pay_page',
    INVOICES_APPROVALS = 'invoices_approvals',
    PARTNERSHIP_CASE = 'partnership_case',
    MEETING_BOOKER_PAGE = 'meeting_booker_page',
    CUSTOMER_CASE = 'customer_case',
    CUSTOMERS_PAGE = 'customers_page',
    DEMO_PAGE = 'demo_page',
    EBOOK = 'ebook',
    EXPENSE_POLICY_BUILDER = 'expense_policy_builder',
    EXPENSE_REPORT_GENERATOR = 'expense_report_generator',
    EXPERIMENTAL_LANDING_PAGE = 'experimental_landing_page',
    EXPLORE_PRODUCT = 'explore_product',
    FAQ_PAGE = 'faq_page',
    FETCH_PAGE = 'fetch_page',
    HOMEPAGE_V4 = 'homepage_v4',
    GETTING_STARTED_EXPERIENCE = 'getting_started_experience',
    LEGAL_PAGE = 'legal_page',
    LEGAL_PAGE_COOKIE_POLICY = 'legal_page_cookie_policy',
    LEGAL_PAGE_DSR = 'legal_page_dsr',
    LEGAL_PAGE_GDPR = 'legal_page_gdpr',
    LEGAL_PAGE_THIRD_PARTY_LICENCES = 'legal_page_third_party_licences',
    NOT_FOUND_PAGE = 'not_found_page',
    ON_DEMAND_RESOURCE_PAGE = 'on_demand_resource_page',
    PARTNER = 'partner',
    PARTNER_BIGFORM_PAGE = 'partner_bigform_page',
    PERKS_INTERNAL_PAGE = 'perks_internal_page',
    PRICING_PAGE = 'pricing_page',
    PRICING_V3_PAGE = 'pricing_v3_page',
    RESOURCE_DETAIL_PAGE = 'resource_detail_page',
    ROI_CALCULATOR = 'roi_calculator',
    SLICE_PAGE = 'slice_page',
    SUBSCRIPTIONS_PAGE = 'subscriptions_page',
    WHY_PLEO_PAGE = 'why_pleo_page',
    SECONDARY_HOMEPAGE = 'secondary_homepage',
    SECONDARY_LEGAL_PAGE = 'secondary_legal_page',
    SECONDARY_PRICING_PAGE = 'secondary_pricing_page',
    SECONDARY_NOT_FOUND_PAGE = 'secondary_not_found_page',
    PRICING_PLAN_PICKER = 'pricing_plan_picker',
}

// Retired /lp/<uid> variants that Astro serves as a dedicated segment page.
// Mirrors the redirect map so CMS links keep their intended audience.
const HOMEPAGE_SEGMENT_PATHS: Record<string, string> = {
    'card-small': '/home-s',
    'enterprise-home': '/home-l',
    'expense-small': '/home-s',
    small: '/home-s',
    'small-home': '/home-s',
    'spend-small': '/home-s',
}

const hasParentPage = (doc: Partial<Document>): doc is Document => {
    return !!doc.data?.parent_document?.uid
}

const hasSecondaryLocaleField = (doc: Partial<Document>): doc is Document => {
    return !!doc.data?.localeCode
}

export const linkResolver: LinkResolver = (doc) => {
    const localize = (path: string) =>
        removeTrailingSlash(
            `/${localeCodeToUrl(doc.lang as LocaleCode)}${path === '/' ? '' : path}`,
        )

    switch (doc.type) {
        // Gatsby no longer builds homepages - Astro serves /<locale>, or a segment page for
        // the former /<locale>/lp/<uid> variants.
        case DocTypesEnum.HOMEPAGE_V4: {
            return localize(HOMEPAGE_SEGMENT_PATHS[doc.uid ?? ''] ?? '/')
        }

        case DocTypesEnum.SECONDARY_HOMEPAGE: {
            if (!hasSecondaryLocaleField(doc)) {
                if (process.env.GATSBY_ACTIVE_ENV === 'production') {
                    throw new Error(`Secondary Homepage is missing localeCode`)
                } else {
                    return 'UNKNOWN_DOCUMENT_TYPE'
                }
            }

            const localeCode = doc.data?.localeCode as LocaleCode

            return `/${localeCodeToUrl(localeCode)}`
        }

        case DocTypesEnum.EBOOK: {
            return localize(`/ebooks/${doc.uid}`)
        }

        case DocTypesEnum.FETCH_PAGE: {
            return localize('/fetch')
        }

        case DocTypesEnum.PERKS_INTERNAL_PAGE: {
            return localize('/perks-internal')
        }

        case DocTypesEnum.SUBSCRIPTIONS_PAGE: {
            return localize('/subscriptions')
        }

        // Gatsby no longer builds pricing pages - Astro serves /<locale>/pricing.
        case DocTypesEnum.PRICING_PAGE:
        case DocTypesEnum.PRICING_V3_PAGE:
        case DocTypesEnum.PRICING_PLAN_PICKER: {
            return localize('/pricing')
        }

        case DocTypesEnum.SECONDARY_PRICING_PAGE: {
            if (!hasSecondaryLocaleField(doc)) {
                if (process.env.GATSBY_ACTIVE_ENV === 'production') {
                    throw new Error(`Secondary Homepage is missing localeCode`)
                } else {
                    return 'UNKNOWN_DOCUMENT_TYPE'
                }
            }

            return `/${localeCodeToUrl(doc.data?.localeCode as LocaleCode)}/pricing`
        }

        case DocTypesEnum.WHY_PLEO_PAGE: {
            return localize('/whypleo')
        }

        case DocTypesEnum.CUSTOMERS_PAGE: {
            return localize('/customers')
        }

        case DocTypesEnum.ABOUT_US_PAGE: {
            return localize('/about')
        }

        case DocTypesEnum.EXPENSE_POLICY_BUILDER: {
            return localize('/expense-policy-builder')
        }

        case DocTypesEnum.ROI_CALCULATOR: {
            return localize('/expense-report-roi-calculator')
        }

        case DocTypesEnum.EXPENSE_REPORT_GENERATOR: {
            return localize('/expense-report-generator')
        }

        case DocTypesEnum.LEGAL_PAGE: {
            return localize('/legal')
        }

        case DocTypesEnum.SECONDARY_LEGAL_PAGE: {
            if (!hasSecondaryLocaleField(doc)) {
                if (process.env.GATSBY_ACTIVE_ENV === 'production') {
                    throw new Error(`Secondary Homepage is missing localeCode`)
                } else {
                    return 'UNKNOWN_DOCUMENT_TYPE'
                }
            }

            return `/${localeCodeToUrl(doc.data?.localeCode as LocaleCode)}/legal`
        }

        case DocTypesEnum.LEGAL_PAGE_GDPR: {
            return localize('/legal/gdpr')
        }

        case DocTypesEnum.LEGAL_PAGE_DSR: {
            return localize('/legal/dsr')
        }

        case DocTypesEnum.LEGAL_PAGE_COOKIE_POLICY: {
            return localize('/legal/cookie-policy')
        }

        case DocTypesEnum.LEGAL_PAGE_THIRD_PARTY_LICENCES: {
            return localize('/legal/third-party-licences')
        }

        case DocTypesEnum.FAQ_PAGE: {
            return localize('/faq')
        }

        case DocTypesEnum.CUSTOMER_CASE: {
            return localize(`/customer-cases/${doc.uid}`)
        }

        case DocTypesEnum.PARTNERSHIP_CASE: {
            return localize(`/partnership-cases/${doc.uid}`)
        }

        case DocTypesEnum.PARTNER: {
            return localize(`/partner/${doc.uid}`)
        }

        case DocTypesEnum.NOT_FOUND_PAGE: {
            return localize('/404')
        }

        case DocTypesEnum.SECONDARY_NOT_FOUND_PAGE: {
            if (!hasSecondaryLocaleField(doc)) {
                if (process.env.GATSBY_ACTIVE_ENV === 'production') {
                    throw new Error(`Secondary Homepage is missing localeCode`)
                } else {
                    return 'UNKNOWN_DOCUMENT_TYPE'
                }
            }

            return `/${localeCodeToUrl(doc.data?.localeCode as LocaleCode)}/404`
        }

        case DocTypesEnum.INVOICES_APPROVALS: {
            return localize('/invoices-approvals')
        }

        case DocTypesEnum.ON_DEMAND_RESOURCE_PAGE: {
            return localize(`/webinars/${doc.uid}`)
        }

        case DocTypesEnum.GETTING_STARTED_EXPERIENCE: {
            return localize('/getting-started')
        }

        case DocTypesEnum.MEETING_BOOKER_PAGE: {
            return localize('/book-a-demo')
        }

        case DocTypesEnum.DEMO_PAGE:
        case DocTypesEnum.PARTNER_BIGFORM_PAGE:
        case DocTypesEnum.APPLE_PAY_PAGE:
        case DocTypesEnum.EXPERIMENTAL_LANDING_PAGE:
        case DocTypesEnum.RESOURCE_DETAIL_PAGE:
        case DocTypesEnum.EXPLORE_PRODUCT: {
            return localize(`/${doc.uid}`)
        }

        case DocTypesEnum.SLICE_PAGE: {
            // relationship fields
            if (doc?.url && doc?.url !== 'UNKNOWN_DOCUMENT_TYPE') {
                return doc.url
            }

            // page generation and rich text links
            if (hasParentPage(doc)) {
                return localize(`/${doc?.data?.parent_document?.uid}/${doc.uid}`)
            }

            return localize(`/${doc.uid}`)
        }

        default: {
            return 'UNKNOWN_DOCUMENT_TYPE'
        }
    }
}
